import React from "react";
import { Linkedin, Award } from "lucide-react";
import * as Icons from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { CORE_VALUES, TEAM, COMPANY } from "../lib/data";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Star;
  return <C className={className} />;
};

export default function About() {
  const { lang } = useApp();
  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.story")}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 max-w-4xl">
          The policy of finance, since <span className="brand-gradient-text">1999</span>.
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mt-6 max-w-3xl leading-relaxed">
          Arthniti, loosely translated as the policy of finance, started as Shreya Financial Services in 1999. Our founder Anup Shah embarked on the glorious journey of becoming a boutique personal financial distribution firm by first starting out as an equity sub-broker. While we aim to reach the skies, our roots are firmly based in our modest upbringing. Servicing your needs has always come first and will continue to do so. Our teams of extremely hardworking and passionate individuals are committed to working in your best interest, for we know that we can't spell success without 'U'.
        </p>
      </section>

      {/* Mission/Vision */}
      <section className="bg-muted/50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Mission</div>
              <p className="font-serif text-xl mt-3 leading-relaxed">
                To empower every individual and family with the knowledge, tools, and trusted guidance they deserve — facilitating access to holistic financial solutions across investments, insurance, retirement, and wealth distribution — so that financial security is not a privilege of the few, but a reality for all who aspire to it.
              </p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">Vision</div>
              <p className="font-serif text-xl mt-3 leading-relaxed">
                To be India's most trusted financial partner — where every rupee finds its purpose, every dream finds its plan, and every generation inherits not just wealth, but the wisdom to grow it.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Values */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.values")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl mt-2 max-w-3xl">Six principles that govern every interaction.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {CORE_VALUES.map((v) => (
            <Card key={v.title} className="card-lift" data-testid={`value-${v.title.replace(/\W/g, '-')}`}>
              <CardContent className="p-7">
                <Icon name={v.icon} className="h-7 w-7 text-primary" />
                <h3 className="font-serif text-xl mt-4">{v.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="bg-card border-y border-border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline text-xs uppercase tracking-[0.2em] font-bold text-primary">{t(lang, "sections.team")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl mt-2">The people behind your portfolio.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {TEAM.map((m) => (
              <Card key={m.name} className="card-lift" data-testid={`team-${m.name.replace(/\W/g, '-')}`}>
                <CardContent className="p-7">
                  {m.photo ? (
                    <div className="h-24 w-24 rounded-full overflow-hidden ring-2 ring-primary/40 ring-offset-2 ring-offset-card">
                      <img src={m.photo} alt={m.name} className="h-full w-full object-cover object-center" />
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-full brand-gradient inline-flex items-center justify-center text-white text-2xl font-serif font-semibold">
                      {m.initials}
                    </div>
                  )}
                  <h3 className="font-serif text-xl mt-5">{m.name}</h3>
                  <p className="text-sm text-primary font-medium">{m.role}</p>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{m.bio}</p>
                  {m.certs && (
                    <div className="mt-4 space-y-1">
                      {m.certs.map((c) => (
                        <div key={c} className="flex items-start gap-2 text-xs text-muted-foreground"><Award className="h-3.5 w-3.5 mt-0.5 text-secondary shrink-0" /><span>{c}</span></div>
                      ))}
                    </div>
                  )}
                  <a href={m.linkedin} target="_blank" rel="noreferrer" data-testid={`team-linkedin-${m.name.replace(/\W/g, '-')}`} className="inline-flex items-center gap-2 mt-5 text-sm text-primary font-medium hover:underline">
                    <Linkedin className="h-4 w-4" /> View LinkedIn
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Card className="border-primary/30 bg-accent/40">
          <CardContent className="p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Regulatory Information</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div><div className="text-xs text-muted-foreground">AMFI Registration</div><div className="font-serif text-xl">{COMPANY.arn}</div></div>
              <div><div className="text-xs text-muted-foreground">SEBI</div><div className="font-serif text-xl">Authorised Person</div></div>
              <div><div className="text-xs text-muted-foreground">Registered Office</div><div className="text-sm">{COMPANY.address}</div></div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
